import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, XCircle, Clock, User, Phone, Mail, CalendarDays, ArrowLeft } from "lucide-react";
import { useAuthState } from "@/hooks/useAuthState";
import { useToast } from "@/hooks/use-toast";
import { BookingService, BookingRequestWithDetails } from "@/services/bookingService";
import { MinhaAreaPageShell } from "@/components/MinhaAreaPageShell";
import { getPropertyImageUrl } from "@/config/api";

const OwnerBookingsInner = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated, userType, userName, userId, loading: authLoading } = useAuthState();
  const [bookingRequests, setBookingRequests] = useState<BookingRequestWithDetails[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || userType !== "owner")) {
      navigate("/");
      return;
    }

    if (isAuthenticated && userType === "owner") {
      fetchBookingRequests();
    }
  }, [isAuthenticated, userType, authLoading, navigate]);

  const fetchBookingRequests = async () => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const requests = await BookingService.getBookingRequestsByOwner(userId);
      setBookingRequests(requests);
    } catch (error) {
      console.error("Erro ao buscar solicitações:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar as solicitações de reserva.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmRequest = async (requestId: number) => {
    try {
      await BookingService.approveBookingRequest(requestId);
      toast({
        title: "Sucesso",
        description: "Reserva confirmada com sucesso!",
      });
      fetchBookingRequests();
      
      // Disparar evento para atualizar notificações no header
      window.dispatchEvent(new CustomEvent('notificationUpdated'));
    } catch (error) {
      console.error("Erro ao confirmar reserva:", error);
      toast({
        title: "Erro",
        description: "Não foi possível confirmar a reserva.",
        variant: "destructive",
      });
    }
  };

  const handleRejectRequest = async (requestId: number) => {
    try {
      await BookingService.rejectBookingRequest(requestId);
      toast({
        title: "Sucesso",
        description: "Reserva rejeitada com sucesso!",
      });
      fetchBookingRequests();
      
      // Disparar evento para atualizar notificações no header
      window.dispatchEvent(new CustomEvent('notificationUpdated'));
    } catch (error) {
      console.error("Erro ao rejeitar reserva:", error);
      toast({
        title: "Erro",
        description: "Não foi possível rejeitar a reserva.",
        variant: "destructive",
      });
    }
  };

  // Formatar datas SEM timezone - usar exatamente como vem do banco
  // Para check_in e check_out (campos DATE): formatar diretamente da string YYYY-MM-DD
  const formatDate = (date: string | Date) => {
    if (!date) return "Data não informada";
    
    // Se for uma string no formato YYYY-MM-DD, formatar diretamente SEM criar Date object
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] = date.split('-');
      return `${day}/${month}/${year}`;
    }
    
    // Se for uma string ISO com hora, extrair apenas a parte da data
    if (typeof date === 'string' && date.includes('T')) {
      const datePart = date.substring(0, 10);
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        const [year, month, day] = datePart.split('-');
        return `${day}/${month}/${year}`;
      }
    }
    
    // Fallback para Date objects (não deve acontecer para check_in/check_out)
    if (date instanceof Date) {
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      return `${day}/${month}/${year}`;
    }
    
    return "Data inválida";
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'confirmed':
        return (
          <Badge variant="default" className="bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            Confirmada
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="destructive">
            <XCircle className="h-3 w-3 mr-1" />
            Rejeitada
          </Badge>
        );
      case 'pending':
      default:
        return (
          <Badge variant="secondary">
            <Clock className="h-3 w-3 mr-1" />
            Pendente
          </Badge>
        );
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando solicitações...</p>
    </div>
    </div>
    );
  }

  return (
    <MinhaAreaPageShell>
<div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate("/minha-area")}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Solicitações de Reservas</h1>
            <p className="text-muted-foreground">
              Gerencie as solicitações de reserva dos seus imóveis
            </p>
    </div>
    </div>
    </div>

      {bookingRequests.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📋</div>
            <h3 className="text-xl font-semibold mb-2">Nenhuma solicitação encontrada</h3>
            <p className="text-muted-foreground">
              Você ainda não possui solicitações de reserva para seus imóveis.
            </p>
    </div>
        </Card>
      ) : (
        <div className="space-y-6">
          {bookingRequests.map((request) => (
            <Card key={request.id} className="overflow-hidden">
              <div className="flex">
                {/* Imagem da capa do imóvel - lado esquerdo */}
                <div className="w-64 flex-shrink-0">
                  {request.cover_image ? (
                    <img 
                      src={getPropertyImageUrl(request.property_id, request.cover_image)} 
                      alt={request.property_title}
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                      <div className="text-center text-gray-500">
                        <div className="text-4xl mb-2">🏠</div>
                        <div className="text-sm">Sem imagem</div>
    </div>
    </div>
                  )}
    </div>
                
                {/* Conteúdo do card - lado direito */}
                <div className="flex-1">
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-xl">{request.property_title}</CardTitle>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(request.status)}
    </div>
    </div>
                  </CardHeader>
                  
                  <CardContent className="pt-0">
                    <div className="grid grid-cols-2 gap-6">
                      {/* Coluna 1: Dados do Hóspede */}
                      <div>
                        <h4 className="font-semibold text-sm text-muted-foreground mb-3 flex items-center gap-2">
                          👤 Dados do Hóspede
                        </h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{request.guest_name}</span>
    </div>
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <span>{request.guest_email}</span>
    </div>
                          {request.guest_phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-muted-foreground" />
                              <span>{request.guest_phone}</span>
    </div>
                          )}
    </div>
    </div>

                      {/* Coluna 2: Dados da Reserva */}
                      <div>
                        <h4 className="font-semibold text-sm text-muted-foreground mb-3 flex items-center gap-2">
                          📅 Dados da Reserva
                        </h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2">
                            <CalendarDays className="h-4 w-4 text-muted-foreground" />
                            <span>Check-in: {formatDate(request.check_in)}</span>
    </div>
                          <div className="flex items-center gap-2">
                            <CalendarDays className="h-4 w-4 text-muted-foreground" />
                            <span>Check-out: {formatDate(request.check_out)}</span>
    </div>
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">Noites:</span>
                            <span className="font-medium">{request.total_nights}</span>
    </div>
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">Valor Total:</span>
                            <span className="font-semibold text-green-600">
                              R$ {request.total_price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </span>
    </div>
    </div>
    </div>
    </div>

                    {/* Observações */}
                    <div className="mt-4 space-y-3">
                      {request.guest_notes && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                          <h4 className="font-semibold text-sm text-yellow-800 mb-1">📝 Observações do Hóspede</h4>
                          <p className="text-sm text-yellow-700">{request.guest_notes}</p>
    </div>
                      )}

                      {request.owner_notes && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <h4 className="font-semibold text-sm text-blue-800 mb-1">💬 Resposta do Proprietário</h4>
                          <p className="text-sm text-blue-700">{request.owner_notes}</p>
    </div>
                      )}
    </div>

                    {request.status === 'pending' && (
                      <div className="flex gap-3 mt-6 pt-4 border-t">
                        <Button
                          onClick={() => handleConfirmRequest(request.id)}
                          className="flex items-center gap-2"
                        >
                          <CheckCircle className="h-4 w-4" />
                          Confirmar
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={() => handleRejectRequest(request.id)}
                          className="flex items-center gap-2"
                        >
                          <XCircle className="h-4 w-4" />
                          Rejeitar
                        </Button>
    </div>
                    )}
                  </CardContent>
    </div>
    </div>
            </Card>
          ))}
    </div>
      )}
    </div>
    </MinhaAreaPageShell>
  );
};

export default OwnerBookingsInner;
